import React from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '@contexts/LanguageContext';
import Icon from '@components/Icon/Icon';
import OptionsManager from './OptionsManager';
import { FIELD_TYPES, AVAILABLE_CONTEXTS } from './constants';

/**
 * Reusable form component for adding or editing a field
 */
const FieldForm = ({
  field,
  onChange,
  onSave,
  onCancel,
  isEditing = false,
  title
}) => {
  const { t } = useLanguage();

  const handleChange = (updates) => {
    onChange({ ...field, ...updates });
  };

  const handleContextToggle = (context) => {
    const currentContexts = field.contexts || [];
    const updatedContexts = currentContexts.includes(context)
      ? currentContexts.filter(c => c !== context)
      : [...currentContexts, context];
    handleChange({ contexts: updatedContexts });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isEditing && !field.key) {
      alert(t('borrowingFields.manager.keyAndLabelRequired') || 'Field key and label are required');
      return;
    }

    if (!field.label) {
      alert(t('borrowingFields.manager.labelRequired') || 'Label is required');
      return;
    }

    if (field.type === 'select' && (!field.options || field.options.length === 0)) {
      alert(t('borrowingFields.manager.selectNeedsOptions') || 'Select field must have at least one option');
      return;
    }

    onSave();
  };

  return (
    <div className="field-form">
      <h5>
        {title || (
          isEditing
            ? <><Icon icon="edit" /> {t('borrowingFields.manager.editField') || 'Edit Field'}</>
            : t('borrowingFields.manager.newField') || 'New Field'
        )}
      </h5>

      <form onSubmit={handleSubmit}>
        {!isEditing ? (
          <div className="form-row">
            <div className="form-group">
              <label>{t('borrowingFields.manager.fieldKey') || 'Field Key'} *</label>
              <input
                type="text"
                value={field.key}
                onChange={(e) => handleChange({
                  key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
                })}
                placeholder="e.g., mileage_at_pickup"
                className="form-control"
              />
              <small>{t('borrowingFields.manager.keyHelp') || 'Unique identifier (lowercase, no spaces)'}</small>
            </div>

            <div className="form-group">
              <label>{t('borrowingFields.manager.fieldType') || 'Type'} *</label>
              <select
                value={field.type}
                onChange={(e) => handleChange({ type: e.target.value })}
                className="form-control"
              >
                {FIELD_TYPES.map(type => (
                  <option key={type} value={type}>
                    {t(`borrowingFields.fieldTypes.${type}`) || type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <>
            <div className="form-group">
              <label>{t('borrowingFields.manager.fieldKey') || 'Field Key'}</label>
              <input
                type="text"
                value={field.key}
                disabled
                className="form-control"
              />
              <small>{t('borrowingFields.manager.keyCannotChange') || 'Field key cannot be changed'}</small>
            </div>

            <div className="form-group">
              <label>{t('borrowingFields.manager.fieldType') || 'Type'} *</label>
              <select
                value={field.type}
                onChange={(e) => handleChange({ type: e.target.value })}
                className="form-control"
              >
                {FIELD_TYPES.map(type => (
                  <option key={type} value={type}>
                    {t(`borrowingFields.fieldTypes.${type}`) || type}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="form-group">
          <label>{t('borrowingFields.manager.label') || 'Label'} *</label>
          <input
            type="text"
            value={field.label}
            onChange={(e) => handleChange({ label: e.target.value })}
            placeholder={t('borrowingFields.manager.labelPlaceholder') || 'Field label shown to users'}
            className="form-control"
          />
        </div>

        <div className="form-group">
          <label>{t('borrowingFields.manager.helpText') || 'Help Text'}</label>
          <input
            type="text"
            value={field.help_text || ''}
            onChange={(e) => handleChange({ help_text: e.target.value })}
            placeholder={t('borrowingFields.manager.helpTextPlaceholder') || 'Optional help text'}
            className="form-control"
          />
        </div>

        <div className="form-group">
          <label>{t('borrowingFields.manager.placeholder') || 'Placeholder'}</label>
          <input
            type="text"
            value={field.placeholder_text || ''}
            onChange={(e) => handleChange({ placeholder_text: e.target.value })}
            placeholder={t('borrowingFields.manager.placeholderExample') || 'e.g., 50000'}
            className="form-control"
          />
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => handleChange({ required: e.target.checked })}
            />
            <span>{t('borrowingFields.manager.required') || 'Required field'}</span>
          </label>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={field.description_field}
              onChange={(e) => handleChange({ description_field: e.target.checked })}
            />
            <span>{t('borrowingFields.manager.descriptionField') || 'Show as description in borrowing card'}</span>
          </label>
        </div>

        <div className="form-group">
          <label>{t('borrowingFields.manager.contexts') || 'When to request this field'} *</label>
          <div className="context-checkboxes">
            {AVAILABLE_CONTEXTS.map(context => (
              <label key={context} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={(field.contexts || []).includes(context)}
                  onChange={() => handleContextToggle(context)}
                />
                <span>{t(`borrowingFields.contexts.${context}`) || context}</span>
              </label>
            ))}
          </div>
          <small>{t('borrowingFields.manager.contextsHelp') || 'Select one or more'}</small>
        </div>

        {field.type === 'select' && (
          <OptionsManager
            options={field.options || []}
            onChange={(options) => handleChange({ options })}
          />
        )}

        <div className="form-actions">
          <button type="submit" className="btn btn-primary">
            <Icon icon="check" /> {t('common.save') || 'Save'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
          >
            <Icon icon="times" /> {t('common.cancel') || 'Cancel'}
          </button>
        </div>
      </form>
    </div>
  );
};

FieldForm.propTypes = {
  field: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
  title: PropTypes.node
};

export default FieldForm;
